// TimePickerDual.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Picker from 'react-mobile-picker';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import InputFormBorder from '../Common/InputFormBorder/InputFormBorder';
import { useSelector } from 'react-redux';
import { getDateFromOffset } from '../../helpers/dates';

const pad2 = (n) => String(n).padStart(2, '0');

const BookSelectorFixed = ({ children }) => {
  const [portalRoot, setPortalRoot] = useState(null);

  useEffect(() => {
    const element = document.getElementById('bookSelector');
    if (element) setPortalRoot(element);
  }, []);

  if (!portalRoot) return null;

  return createPortal(<div>{children}</div>, portalRoot);
};

export default function TimePickerDual({
  placeholder,
  value,
  onChange,
  minuteStep = 5,
  wheelMode = 'normal',
  disabled = false,
  minHour = null,
  minMinute = null,
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const [selectedTime, setSelectedTime] = useState(value || null);

  const user = useSelector((state) => state.associates.user);
  const timeRightNow = new Date(getDateFromOffset(user.companyUtcMinutesOffset));

  const initialHour = pad2(timeRightNow.getHours());
  const initialMinute = pad2(Math.floor(timeRightNow.getMinutes() / minuteStep) * minuteStep);

  const [map, setMap] = useState({
    hour: value ? pad2(value.hour) : initialHour,
    minute: value ? pad2(value.minute) : initialMinute,
  });

  const lastMapRef = useRef(map);

  const hourOptions = useMemo(() => {
    let base = Array.from({ length: 24 }, (_, h) => pad2(h));
    if (minHour !== null) base = base.filter((h) => parseInt(h) >= minHour);
    return base;
  }, [minHour]);

  const minuteOptions = useMemo(() => {
    const base = [];
    for (let m = 0; m < 60; m += minuteStep) base.push(pad2(m));

    if (minHour === null) return base;
    if (parseInt(map.hour) > minHour) return base;
    return base.filter((m) => parseInt(m) >= (minMinute ?? 0));
  }, [minuteStep, map.hour, minHour, minMinute]);

  useEffect(() => {
    if (selectedTime) {
      setMap({
        hour: pad2(selectedTime.hour),
        minute: pad2(selectedTime.minute),
      });
      lastMapRef.current = {
        hour: pad2(selectedTime.hour),
        minute: pad2(selectedTime.minute),
      };
    }
  }, [selectedTime]);

  const emit = (next) => {
    const num = {
      hour: parseInt(next.hour, 10),
      minute: parseInt(next.minute, 10),
    };
    setSelectedTime(num);
    onChange?.(num);
  };

  const handleChange = (next) => {
    setMap(next);

    if (next.hour !== lastMapRef.current.hour || next.minute !== lastMapRef.current.minute) {
      emit(next);
    }

    lastMapRef.current = next;
  };

  const onHandleDone = () => {
    emit(map);
    setIsOpen(false);
  };

  return (
    <>
      <div
        className='w-100'
        onClick={(e) => {
          e.preventDefault();
          if (!disabled) setIsOpen(true); 
        }}
        style={disabled ? { opacity: 0.5, pointerEvents: 'none' } : {}}
      >
        <InputFormBorder
          placeholder={placeholder}
          value={selectedTime ? `${pad2(selectedTime.hour)}:${pad2(selectedTime.minute)}` : ''}
          disableFocus={true}
          disabled={disabled} 
          readOnly
          inputMode="none"
        />
      </div>
      <BookSelectorFixed>
        <div className={`book-selector font-medium ${isOpen ? 'open' : ''}`}>
          <div className='header-container py-2 px-3' onClick={() => onHandleDone()}>
            {t('done')}
          </div>
          <div
            className='book-selector-content'
            aria-label='Time picker'
            aria-disabled={disabled}
            style={disabled ? { opacity: 0.4, pointerEvents: 'none' } : {}}
          >
            <Picker
              value={map}
              onChange={handleChange}
              wheelMode={wheelMode}
              disabled={disabled} 
            >
              <Picker.Column name='hour'>
                {hourOptions.map((h) => (
                  <Picker.Item key={h} value={h} aria-label={`${h} hours`}>
                    {({ selected }) => (
                      <div
                        className='text-right w-100 mr-2'
                        style={{ color: selected ? '#fff' : '#7a7676' }}
                      >
                        {h}
                      </div>
                    )}
                  </Picker.Item>
                ))}
              </Picker.Column>
              <div className='d-flex align-items-center mb-1 text-white'>:</div>
              <Picker.Column name='minute'>
                {minuteOptions.map((m) => (
                  <Picker.Item key={m} value={m} aria-label={`${m} minutes`}>
                    {({ selected }) => (
                      <div
                        className='text-left w-100 ml-2'
                        style={{ color: selected ? '#fff' : '#7a7676' }}
                      >
                        {m}
                      </div>
                    )}
                  </Picker.Item>
                ))}
              </Picker.Column>
            </Picker>
          </div>
        </div>
      </BookSelectorFixed>
    </>
  );
}
